import React, { useState, useEffect } from "react";

import OrderItem from "components/OrderItem";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import Input from "components/Input";
import TwoCellRow from "components/TwoCellRow";
import FormatDateWithLocale from "components/FormatDateWithLocale";
import { acceptOrder } from "api";
import cn from "classnames";
import { calculationsFor1to8M, useFormattedMessage, useSideModalScrollToTop } from "hooks";
import { sendFormError } from "i18n/messages/errors";
import { numberRegex } from "constants/validationRegex";
import InvestmentChecks from "components/1to8MillionInvestments/InvestmentChecks";
import { RESPONSE_ERRORS } from "constants/index";

const messages = {
    requiredField: "required_field",
    boOTPAcceptMainHeading: "buy_digital_shares",
    soEditWalletHeading: "accept_sales_offer",
    soEditWalletButton: "sales_offer_accept",
    soAcceptTileTitle: "your_choice",
    soAcceptTitle1: "buy",
    soAcceptTitle2: "shares_now_for",
    soAcceptTitle3: "bo_accept_title3",
    boAcceptContent: "sales_offer_approved",
    boAcceptShareLabel: "how_many_edit_digital_share_buy",
    boEditDigitalSharesPlaceholder: "number_of_shares",
    totalPurchasePrice: "total_purchase_price",
    notEnoughBalance: "not_enough_balance",
    minimumamountofshares: "minimum_amount_of_shares",
    invalidShares: "invalid_shares",
    shareShortText: "pc",
    minimumUserShares: "minimum_user_shares",
    sharesExceeds: "shares_exceeds",
    sharesAvailableAgainText: "all_digital_shares_reserved",
    investmentSumFormMax25k: "investment_sum_max25k",
    investmnetChangedInMeantime: "update_your_information",
    minInvestShares1: "min_invest_shares1",
    minInvestShares2: "min_invest_shares2",
    maxInvestAmountReached: "maximum_investment_amount_reached",
    interestedInHigherInvestment: "interested_in_higher_investment",
};

const leftClass = "mb-9 d-block";
const rightClass = "text-14 fw-600 d-block mb-9";

const AcceptOrder = ({
    className = "",
    onBack,
    onForward,
    setCloseTitle,
    payInAmount,
    stepData,
    backSlideEffect = "",
    minShareTypeWise,
    maxInvestAmount,
    isInvestment1to8M,
    investmentAmountData,
    fetchInvestmentAmountData,
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    const [isSubmitting, setSubmitting] = useState("");
    const [splitShares, setSplitShares] = useState(stepData.shares);
    const [totalPurchasePrice, setTotalPurchasePrice] = useState(0);
    const [status, setStatus] = useState({});
    const [requestError, setRequestError] = useState();
    const [touched, setFieldTouched] = useState(false);
    const [conditions, setConditions] = useState({ moreThan100kFreelyDisposable: false, notMoreThanDoubleMonthlyIncome: false });

    useEffect(() => {
        setCloseTitle(formatMessage(messages.boOTPAcceptMainHeading));
    }, []);

    useEffect(() => {
        if (splitShares || stepData.price || stepData.pricePerShare) {
            const price = splitShares !== "" ? parseInt(splitShares) * parseFloat(stepData.pricePerShare) : stepData.price;
            price && setTotalPurchasePrice(price);
        }
    }, [splitShares, stepData.price, stepData.pricePerShare]);

    const sharesErrorMessage = (_minInvestShare) => {
        return `${formatMessage(messages.minInvestShares1)} ${_minInvestShare} ${formatMessage(messages.minInvestShares2)}`;
    };

    useEffect(() => {
        if (totalPurchasePrice && payInAmount) {
            if (totalPurchasePrice > payInAmount) {
                setRequestError(formatMessage(messages.notEnoughBalance));
            } else if (splitShares !== "" && !numberRegex.test(splitShares)) {
                setRequestError(formatMessage(messages.invalidShares));
            } else if (splitShares !== "" && parseInt(splitShares) < minShareTypeWise) {
                setRequestError(sharesErrorMessage(minShareTypeWise));
            } else if (splitShares !== "" && parseInt(splitShares) - parseInt(stepData.shares) > 0) {
                setRequestError(formatMessage(messages.sharesExceeds));
            } else if (
                splitShares !== "" &&
                parseInt(stepData.shares) - parseInt(splitShares) !== 0 &&
                parseInt(stepData.shares) - parseInt(splitShares) < minShareTypeWise
            ) {
                setRequestError(formatMessage(messages.minimumUserShares));
            } else {
                clearError();
            }
        }
    }, [totalPurchasePrice, payInAmount, splitShares]);

    useEffect(() => {
        if (status?.requestError) {
            setRequestError(
                status.requestError === "notEnoughBalance"
                    ? formatMessage(messages.notEnoughBalance)
                    : status.requestError === "minimumAmountOfShares"
                      ? sharesErrorMessage(minShareTypeWise)
                      : formatMessage(sendFormError),
            );
        }
    }, [status]);

    const onConditionsChanged = (key, value) => {
        conditions[key] = value;
        setConditions({ ...conditions });
    };
    function getCalculation(totalAmount, conditions, maxInvestAmount) {
        return calculationsFor1to8M(totalAmount, conditions, maxInvestAmount);
    }

    const onSubmit = () => {
        try {
            setSubmitting(true);
            const shares = stepData.split ? splitShares : stepData.shares;
            const totalAmount = shares * parseFloat(stepData.pricePerShare) + investmentAmountData;
            const { isMoreThan1k, isMoreThan10k } = getCalculation(totalAmount, conditions, maxInvestAmount);

            const additionalParams = isMoreThan1k
                ? {
                      SelfDeclarationMoreThan100000FreelyDisposable: `${conditions.moreThan100kFreelyDisposable}`,
                      SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${conditions.notMoreThanDoubleMonthlyIncome}`,
                  }
                : isMoreThan10k
                  ? {
                        SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${conditions.notMoreThanDoubleMonthlyIncome}`,
                    }
                  : {};

            const variables = {
                order: {
                    uid: stepData.uid,
                    Shares: splitShares !== "" ? splitShares : stepData.shares,
                    ...additionalParams,
                },
            };

            acceptOrder(variables)
                .then((res) => {
                    res && setSubmitting(false);

                    if (res?.data?.errors?.length) {
                        setStatus({ requestError: res.data.errors[0].message });
                    }

                    if (
                        !res ||
                        res.errors ||
                        res.data.errors ||
                        (res.data.data.errors && res?.data?.errors?.[0]?.message == RESPONSE_ERRORS.SELF_DECLARATION_ERROR)
                    ) {
                        fetchInvestmentAmountData();
                        setRequestError(formatMessage(messages.investmnetChangedInMeantime));
                    }

                    const order = res?.data?.data?.acceptOrder?.order;
                    return onForward(order);
                })
                .catch(() => {
                    setStatus({ requestError: true });
                    setSubmitting(false);
                });
        } catch (error) {
            setStatus({ requestError: true });
            setSubmitting(false);
        }
    };

    const clearError = () => {
        setStatus({});
        setRequestError("");
    };

    //ADDITIONAL CHECKS FOR 1 TO 8 MILLION INVESTMENT
    const shares = stepData.split ? splitShares : stepData.shares;
    const totalAmount = shares * parseFloat(stepData.pricePerShare) + investmentAmountData;

    const { isMoreThan1k, isMoreThan10k, isMoreThan25k, is1KConditionIncomeplete, is10kConditionIncomeplete } =
        calculationsFor1to8M(totalAmount, conditions, maxInvestAmount);

    const isButtonDisableWith1to8M = isInvestment1to8M
        ? is1KConditionIncomeplete || is10kConditionIncomeplete || isMoreThan25k
        : false;

    // useEffect(() => {
    //     if (isMoreThan1k || isMoreThan10k) {
    //         onConditionsChanged("moreThan100kFreelyDisposable", false)
    //         onConditionsChanged("notMoreThanDoubleMonthlyIncome", false)
    //     }
    // }, [splitShares])

    return (
        <>
            <div className={`buy-orders-container ${className} ${backSlideEffect}`}>
                <h3>{formatMessage(messages.soEditWalletHeading)}</h3>
                <h4 className="mt-10">{formatMessage(messages.soAcceptTileTitle)}</h4>
                <OrderItem
                    equalTitle={false}
                    orderFlag={stepData.flag}
                    split={stepData.split}
                    tag={stepData.tag}
                    className="mt-0 mb-10 border-active-blue"
                    image={stepData.image}
                    title={stepData.title}
                    // city={stepData.city}
                    shares={stepData.shares}
                    price={stepData.price}
                    date={<FormatDateWithLocale date={stepData.date} />}
                    perSharePrice={stepData.pricePerShare}
                />

                {parseInt(stepData.shares) === 0 ? (
                    <p>{formatMessage(messages.sharesAvailableAgainText)}</p>
                ) : (
                    <p>
                        {formatMessage(messages.soAcceptTitle1)} <CustomFormatNumber value={stepData.shares} fixed="0" noSymbol />{" "}
                        {formatMessage(messages.soAcceptTitle2)} {<CustomFormatNumber value={stepData.price} />}
                        {formatMessage(messages.soAcceptTitle3)}
                    </p>
                )}

                {stepData.split && parseInt(stepData.shares) >= minShareTypeWise * 2 ? (
                    <>
                        <p>{formatMessage(messages.boAcceptContent)}</p>
                        <div className="mt-10">
                            <Input
                                label={formatMessage(messages.boAcceptShareLabel)}
                                className="mb-6"
                                type="text"
                                name="splitShares"
                                placeholder={formatMessage(messages.boEditDigitalSharesPlaceholder)}
                                inputSymbol={formatMessage(messages.shareShortText)}
                                inputSymbolClass="black-color"
                                value={splitShares}
                                onChange={(e) => {
                                    setSplitShares(e.target.value), setFieldTouched(true);
                                }}
                                // onBlur={handleBlur}
                                // errorMessage={errors.splitShares ? formatMessage(messages[errors.splitShares]) : null}
                                error={touched && requestError}
                            />
                        </div>
                    </>
                ) : null}
                <TwoCellRow
                    removeLClass
                    removeRClass
                    lClassname={leftClass}
                    rClassname={rightClass}
                    left={formatMessage(messages.totalPurchasePrice)}
                    right={<CustomFormatNumber value={totalPurchasePrice || stepData.price} />}
                />
            </div>
            {requestError && <span className="error pl-0">{requestError}</span>}
            {isInvestment1to8M && investmentAmountData == maxInvestAmount && (
                <label className="alert-red-color text-left mt-0 mb-2">
                    {formatMessage(messages.maxInvestAmountReached)}
                    {maxInvestAmount}
                    {formatMessage(messages.interestedInHigherInvestment)}
                </label>
            )}
            {(isMoreThan1k || isMoreThan10k) && isInvestment1to8M && (
                <InvestmentChecks
                    moreThan100kFreelyDisposable={conditions.moreThan100kFreelyDisposable}
                    notMoreThanDoubleMonthlyIncome={conditions.notMoreThanDoubleMonthlyIncome}
                    isMoreThan10k={isMoreThan10k}
                    isMoreThan1k={isMoreThan1k}
                    setFieldValue={onConditionsChanged}
                    isLoading={isSubmitting}
                />
            )}
            <div className="bottom-bar-container single-button">
                <PrevButton disable={isSubmitting} onClick={onBack} />
                <NextButton
                    className={cn({
                        "mw-100 mr-1": true,
                        "disable-grey":
                            parseInt(splitShares) === 0 ||
                            isSubmitting ||
                            requestError ||
                            isButtonDisableWith1to8M ||
                            investmentAmountData == maxInvestAmount,
                    })}
                    disable={
                        parseInt(splitShares) === 0 ||
                        isSubmitting ||
                        requestError ||
                        isButtonDisableWith1to8M ||
                        investmentAmountData == maxInvestAmount
                    }
                    title={formatMessage(messages.soEditWalletButton)}
                    onClick={onSubmit}
                    isLoading={isSubmitting}
                />
            </div>
        </>
    );
};

export default AcceptOrder;
